/** @format */

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import Loader from "../../elements/Loader";
import Pagination from "../../elements/Pagination";

export default function Payments() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState(() => sessionStorage.getItem('payments_fromDate') || '');
  const [toDate, setToDate] = useState(() => sessionStorage.getItem('payments_toDate') || '');

  const getPaymentList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `payment/list?page=${currentPage + 1}&limit=10&search=${searchTerm}`;
      if (fromDate) url += `&fromDate=${fromDate}`;
      if (toDate) url += `&toDate=${toDate}`;
      const response = await ApiService.get(url);
      setLoading(false);
      if (response && response.result) {
        const dataWithIST = response.result.data.map((dp) => ({
          ...dp,
          updatedAt: convertToIST(dp.updatedAt),
          createdAt: convertToIST(dp.createdAt),
        }));
        setPayments(dataWithIST);
        setPageCount(response.result.totalPages);
      } else {
        setPayments([]);
        setPageCount(0);
      }
    } catch (err) {
      setLoading(false);
      toast.error(err?.response?.data?.error || "Failed to fetch payment data");
    }
  }, [currentPage, searchTerm, fromDate, toDate]);

  useEffect(() => {
    getPaymentList();
  }, [getPaymentList]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handleFromDateChange = (e) => {
    const val = e.target.value;
    setFromDate(val);
    sessionStorage.setItem('payments_fromDate', val);
  };

  const handleToDateChange = (e) => {
    const val = e.target.value;
    setToDate(val);
    sessionStorage.setItem('payments_toDate', val);
  };

  const handleDateFilter = () => {
    setCurrentPage(0);
    getPaymentList();
  };

  const clearDateFilter = () => {
    setFromDate('');
    setToDate('');
    setCurrentPage(0);
    sessionStorage.removeItem('payments_fromDate');
    sessionStorage.removeItem('payments_toDate');
  };

  const convertToIST = (utcDate) => {
    if (!utcDate) return "NA";
    const date = new Date(utcDate);
    return date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  };

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };

  return (
    <div className="row">
      <div className="col-xl-12">
        {loading && <Loader />}
        <div className="card dz-card">
          <div className="card-header flex-wrap d-flex justify-content-between border-0">
            <div>
              <h4 className="card-title">Payment & Invoicing Management</h4>
            </div>
            <div className="d-flex align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 fw-semibold" style={{ whiteSpace: 'nowrap' }}>Date:</label>
                <input
                  type="date"
                  className="form-control"
                  style={{ width: '160px' }}
                  value={fromDate}
                  onChange={handleFromDateChange}
                />
                <span className="mb-0">to</span>
                <input
                  type="date"
                  className="form-control"
                  style={{ width: '160px' }}
                  value={toDate}
                  onChange={handleToDateChange}
                />
                <button className="btn btn-primary btn-sm" onClick={handleDateFilter}>Apply</button>
                {(fromDate || toDate) && (
                  <button className="btn btn-outline-secondary btn-sm" onClick={clearDateFilter}>Clear</button>
                )}
              </div>
              <div className="input-group search-area" style={{ width: '250px' }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by ID, Name or Phone..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
                <span className="input-group-text">
                  <i className="flaticon-381-search-2"></i>
                </span>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-responsive-md">
                <thead>
                  <tr>
                    <th><strong>Sr No.</strong></th>
                    <th><strong>Booking ID</strong></th>
                    <th><strong>Customer Name</strong></th>
                    <th><strong>Service</strong></th>
                    <th><strong>Total Price</strong></th>
                    <th><strong>Payment Status</strong></th>
                    <th><strong>Invoice Status</strong></th>
                    <th><strong>Proforma</strong></th>
                    <th><strong>Tax Invoice</strong></th>
                    <th><strong>Pandit Receipt</strong></th>
                    <th><strong>Action</strong></th>
                  </tr>
                </thead>
                <tbody>
                  {payments && payments.length > 0 ? (
                    payments.map((dp, key) => (
                      <tr key={key}>
                        <td>{key + 1 + (currentPage * 10)}</td>
                        <td><span className="badge badge-light">{dp.bookingId?.slice(0, 8)}...</span></td>
                        <td>{dp.user?.name || "NA"}</td>
                        <td>{dp.service?.title || "NA"}</td>
                        <td>₹{dp.finalPrice || dp.actualPrice}</td>
                        <td>
                          <span className={`badge ${dp.paymentStatus === 'paid' ? 'badge-success' : 'badge-warning'}`}>
                            {dp.paymentStatus}
                          </span>
                        </td>
                        <td>{dp.invoiceStatus || "pending"}</td>
                        <td>
                          {dp.proformaInvoiceUrl ? (
                            <a href={dp.proformaInvoiceUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-xxs">View</a>
                          ) : "N/A"}
                        </td>
                        <td>
                          {dp.finalInvoiceUrl ? (
                            <a href={dp.finalInvoiceUrl} target="_blank" rel="noreferrer" className="btn btn-outline-success btn-xxs">View</a>
                          ) : "N/A"}
                        </td>
                        <td>
                          {dp.panditInvoiceUrl ? (
                            <a href={dp.panditInvoiceUrl} target="_blank" rel="noreferrer" className="btn btn-outline-info btn-xxs">View</a>
                          ) : "N/A"}
                        </td>
                        <td>
                          <button 
                            className="btn btn-primary shadow btn-xs sharp"
                            onClick={() => navigate(`/audit-payment/${dp.bookingId}`)}
                          >
                            <i className="fa fa-eye" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center">No payment records found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              <Pagination pageCount={pageCount} handlePageClick={handlePageClick} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
